import { useEffect, useMemo, useState } from "react";
import { MenuItem, TextField, Typography } from "@mui/material";

/**
 * SPLITTING THE VENDOR'S DISCOUNT.
 *
 * Under the offer model the vendor types what they charge US, floored by the
 * discount they owe off the industry price. Whatever they actually gave is a
 * POOL, and this is where it is divided:
 *
 *   industry      200
 *   their offer   160    (the pool is 40 — what they gave us)
 *   we keep        24
 *   buyer pays    184    = offer + keep
 *   passed on      16    = the buyer's discount off list
 *
 * A vendor who over-delivers widens the pool rather than being held to what
 * they owed. The split is decided against what they actually gave.
 *
 * The form follows the agreement: a percentage deal splits in percent, a flat
 * deal in rupees, so the two numbers can be checked against each other by eye.
 *
 * Keeping the whole pool is a real choice (the buyer simply pays list), which
 * is why it is the explicit default rather than something that happens when
 * nobody looks.
 */

const pctLabel = (n) => {
  const v = Number(n) * 100;
  if (!Number.isFinite(v)) return "—";
  return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
};

const money = (n, currency) => {
  const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : "₹";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return (
    sym +
    v.toLocaleString(!currency || currency === "INR" ? "en-IN" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/** Mirrors utils/offerPricing.js — the preview must not drift from the publish. */
const keepAmount = (industry, pool, kind, value) => {
  const ind = Number(industry);
  const p = Number(pool);
  const v = Number(value);
  if (!Number.isFinite(ind) || !Number.isFinite(p)) return null;
  if (!Number.isFinite(v)) return p;
  const raw = kind === "flat" ? v : ind * v;
  return Math.round(Math.min(Math.max(0, raw), p) * 100) / 100;
};

export default function DiscountSplitControl({
  /** `margin` from get_vendor_product — carries the agreement and any split. */
  margin,
  /** A representative tier from this submission: { industry, offer }. */
  sample,
  currency,
  disabled,
  onChange,
}) {
  const agreed = margin?.split || null;
  // Percentage deals split in percent, flat deals in rupees.
  const form = margin?.effective?.kind === "flat" ? "flat" : "rate";

  const [kind, setKind] = useState(agreed ? agreed.kind : "none");
  const [raw, setRaw] = useState(() => {
    if (!agreed) return "";
    return agreed.kind === "rate" ? String(Number(agreed.value) * 100) : String(agreed.value);
  });

  useEffect(() => {
    if (kind === "none") {
      onChange?.({ kind: "none" });
      return;
    }
    const n = Number(raw);
    if (raw === "" || !Number.isFinite(n)) {
      onChange?.(null);
      return;
    }
    onChange?.({ kind, value: kind === "rate" ? n / 100 : n });
    // onChange is an inline arrow at the call site; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, raw]);

  const invalid =
    kind !== "none" &&
    (raw === "" ||
      !Number.isFinite(Number(raw)) ||
      Number(raw) < 0 ||
      (kind === "rate" && Number(raw) >= 100));

  const preview = useMemo(() => {
    const industry = Number(sample?.industry);
    const offer = Number(sample?.offer);
    if (!Number.isFinite(industry) || !Number.isFinite(offer) || industry <= 0) {
      return null;
    }
    const pool = Math.round(Math.max(0, industry - offer) * 100) / 100;
    const n = Number(raw);
    const keep =
      kind === "none" || invalid
        ? pool
        : keepAmount(industry, pool, kind, kind === "rate" ? n / 100 : n);
    if (keep === null) return null;
    return {
      industry,
      offer,
      pool,
      keep,
      passed: Math.round((pool - keep) * 100) / 100,
      buyer: Math.round((offer + keep) * 100) / 100,
    };
  }, [sample, kind, raw, invalid]);

  if (margin?.house) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 2 }}>
        Splitting their discount
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: "block", marginBottom: 8 }}
      >
        They priced against the industry price. Decide how much of the discount
        they gave Easexpense keeps — the rest reaches the buyer as a saving off
        list.
      </Typography>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <TextField
          select
          size="small"
          label="We keep"
          value={kind}
          disabled={disabled}
          onChange={(e) => setKind(e.target.value)}
          style={{ minWidth: 210 }}
        >
          <MenuItem value="none">All of it — buyer pays list</MenuItem>
          {form === "rate" ? (
            <MenuItem value="rate">A percentage of list</MenuItem>
          ) : (
            <MenuItem value="flat">A flat amount per unit</MenuItem>
          )}
        </TextField>

        {kind !== "none" && (
          <TextField
            size="small"
            type="number"
            label={kind === "rate" ? "Percent of list" : "Amount per unit"}
            value={raw}
            disabled={disabled}
            error={invalid}
            onChange={(e) => setRaw(e.target.value)}
            onWheel={(e) => e.target.blur()}
            inputProps={{ min: 0, step: kind === "rate" ? 0.5 : 1 }}
            style={{ width: 170 }}
          />
        )}
      </div>

      {invalid && (
        <Typography variant="caption" style={{ display: "block", marginTop: 6, color: "#c53030" }}>
          {kind === "rate"
            ? "A percentage has to sit between 0 and 100."
            : "A flat amount cannot be negative."}
        </Typography>
      )}

      {/* The arithmetic, on a real tier from this submission. This is the only
          place the buyer price appears before it is published. */}
      {preview && (
        <div
          style={{
            marginTop: 10,
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12.5,
            lineHeight: 1.7,
            color: "#2d3748",
          }}
        >
          <div>
            Industry price <strong>{money(preview.industry, currency)}</strong>
          </div>
          <div>
            They charge us <strong>{money(preview.offer, currency)}</strong> — a
            discount of {money(preview.pool, currency)}
            {preview.industry > 0
              ? ` (${pctLabel(preview.pool / preview.industry)})`
              : ""}
          </div>
          <div>
            Easexpense keeps <strong>{money(preview.keep, currency)}</strong>,
            buyer saves {money(preview.passed, currency)}
          </div>
          <div style={{ marginTop: 2, borderTop: "1px solid #edf2f7", paddingTop: 4 }}>
            <strong>Buyer pays {money(preview.buyer, currency)}</strong>
          </div>
        </div>
      )}
    </div>
  );
}
